import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useMemo } from 'react';

export const GET_FIELD_SELECTIONS = gql`
  query GetFieldSelections($model: String!, $fieldName: String!) {
    getFieldSelections(model: $model, fieldName: $fieldName) {
      id
      name
      value
      sequence
    }
  }
`;

export interface FieldSelectionNode {
  id: string;
  name: string;
  sequence: number;
  value: string;
}

export interface GetFieldSelectionsData {
  getFieldSelections: FieldSelectionNode[];
}

export interface GetFieldSelectionsVariables {
  fieldName: string;
  model: string;
}

export interface SelectOption {
  label: string;
  value: string;
}

export interface UseFieldSelectionsResult {
  getLabel: (value: string) => string;
  labelMap: Map<string, string>;
  loading: boolean;
  options: SelectOption[];
}

/**
 * Hook to fetch field selection options from the backend
 * @param model - The Odoo model name (e.g., "opstream.murano.tracking")
 * @param fieldName - The field name (e.g., "murano_status")
 * @returns options array and loading state
 */
export function useFieldSelections(
  model: string,
  fieldName: string
): UseFieldSelectionsResult {
  const { data, loading } = useQuery<
    GetFieldSelectionsData,
    GetFieldSelectionsVariables
  >(GET_FIELD_SELECTIONS, {
    fetchPolicy: 'cache-first',
    skip: !model || !fieldName,
    variables: { fieldName, model }
  });

  const options = useMemo<SelectOption[]>(() => {
    if (!data?.getFieldSelections) return [];

    return [...data.getFieldSelections]
      .sort((a, b) => a.sequence - b.sequence)
      .map(item => ({
        label: item.name,
        value: item.value
      }));
  }, [data]);

  // Create a map for quick label lookup by value
  const labelMap = useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>();
    if (data?.getFieldSelections) {
      for (const item of data.getFieldSelections) {
        map.set(item.value, item.name);
      }
    }
    return map;
  }, [data]);

  // Helper function to get label for a value
  const getLabel = (value: string): string => {
    return labelMap.get(value) ?? value;
  };

  const result: UseFieldSelectionsResult = {
    getLabel,
    labelMap,
    loading,
    options
  };
  return result;
}
